import { DateTime } from 'luxon'
import { GenericResponse, RegistrationResponse, PrivacyResponse, PubblicitaProfilataResponse, PersonalizzazioneResponse, OauthResponse, OauthGenericResponse, OauthCodeResponse, LoginResponseLogin, DeviceResponse, Device, DeviceDetail, UserUpdate, userDataUpdateFieldTypes, userDataUpdateFieldSubtypes, genders, DateString, DateTimeString, UserDetail, UserDataUpdate, DownloadResponse } from "./service-models.js"
import { z } from 'zod'

export const genericResponseType = z.object({
  message: z.string().optional(),
  response: z.enum(['OK', 'KO']).optional(),
  title: z.string().optional(),
  detail: z.string().optional(),
  esito: z.enum(['OK', 'KO', 'true', 'false']).optional()
})

export const genericResponseSchema: z.ZodType<GenericResponse> = genericResponseType

const fieldOptionSchema = z.strictObject({
  key: z.string(),
  value: z.string()
}).partial()

const userDataUpdateFieldSchema = z.object({
  index: z.number(),
  type: z.enum(userDataUpdateFieldTypes),
  subType: z.enum(userDataUpdateFieldSubtypes),
  label: z.string(),
  code: z.string(),
  format: z.string(),
  values: z.array(fieldOptionSchema),
  validators: z.record(z.string()),
  current_value: z.string()
}).partial()

const userUpdateType = z.object({
  title: z.string(),
  name: z.string(),
  message: z.string(),
  footer: z.string(),
  submit: z.string(),
  user_data_token: z.string(),
  fields: z.array(userDataUpdateFieldSchema).optional()
}).partial()

export const userUpdateSchema: z.ZodType<UserUpdate> = userUpdateType

export const userDataUpdateSchema: Zod.ZodType<UserDataUpdate> = userUpdateType.extend({
  fields: z.array(userDataUpdateFieldSchema).optional()
}).partial();

interface PositiveNumber {
  readonly Positive: unique symbol
}
export type Positive = number & PositiveNumber;
const isPositive = (arg: number): arg is Positive => {
  return arg >= 0;
}
const positive = z.number().refine(isPositive, arg => ({ message: `${arg} non è un numero positivo` }))

const isDateString = (s: unknown): s is DateString => {
  if (typeof s !== 'string') { return false }
  return DateTime.fromFormat(s, 'yyyy-MM-dd').isValid
}
const dateString = z.custom<DateString>(val => isDateString(val))

const isDateTimeString = (s: unknown): s is DateTimeString => {
  if (typeof s !== 'string') { return false }
  return DateTime.fromISO(s).isValid
}
const dateTimeString = z.custom<DateTimeString>(val => isDateTimeString(val))

export const userDetailSchema: z.ZodType<UserDetail> = z.object({
  ua: z.string(),
  age: positive,
  birthDate: dateString,
  gender: z.enum(genders),
  uid: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  lid: z.string(),
  userDomain: z.object({
    refreshToken: z.string()
  }),
  photoURL: z.string().optional().nullable(),
  lastToken: z.union([z.null(), dateTimeString]).optional(),
  pubblicitaProfilata: z.boolean().optional(),
  pubblicitaProfilataV: z.string().optional().or(z.null()),
  privacyPubblicita: z.boolean().optional(),
  privacyPolicy: z.boolean().optional(),
  personalizzazione: z.boolean().optional(),
  privacy_Policy_isAccepted: z.boolean().optional(),
  privacy_Pubblicita_isAccepted: z.boolean().optional(),
  user_update: z.array(userUpdateSchema).optional().or(z.null()),
  user_data_update: userDataUpdateSchema.optional().nullable(),
  parental_control: z.object({
    pin: z.string(),
    level: z.number(),
    created_date: dateTimeString,
    last_modified_date: dateTimeString.nullable()
  }).nullable().optional()
})

const socialSchema = z.object({
  loginProvider: z.string(),
  photoURL: z.string().optional().nullable(),
  thumbnailURL: z.string().optional().nullable()
})

export const registrationResponseSchema: z.ZodType<RegistrationResponse> = genericResponseType.extend({
  authorization: z.string().optional(),
  ente_accreditato: z.boolean().optional(),
  raisso: userDetailSchema.optional(),
  refreshToken: z.string().optional(),
  social: socialSchema.optional(),
  ua: z.string().optional()
})

const privacyResponseType = genericResponseType.partial().extend({
  pubblicitaProfilata: z.boolean().optional(),
  pubblicitaProfilataV: z.string().optional(),
  privacyPubblicita: z.boolean().optional(),
  privacyPolicy: z.boolean().optional(),
  personalizzazione: z.boolean().optional(),
  privacy_Policy_isAccepted: z.boolean().optional(),
  privacy_Pubblicita_isAccepted: z.boolean().optional(),
})
export const privacyResponseSchema: z.ZodType<PrivacyResponse> = privacyResponseType;

export const pubblicitaProfilataResponseSchema: z.ZodType<PubblicitaProfilataResponse> = privacyResponseType.pick({
  pubblicitaProfilata: true,
  pubblicitaProfilataV: true,
  privacyPubblicita: true,
  privacy_Pubblicita_isAccepted: true
}).partial()

export const personalizzazioneResponseSchema: z.ZodType<PersonalizzazioneResponse> = z.strictObject({
  personalizzazione: z.boolean()
})

export const oauthResponseSchema: z.ZodType<OauthResponse> = z.strictObject({
  scope: z.string(),
  refresh_token: z.string(),
  access_token: z.string(),
  expires_in: z.number().gte(0),
  token_type: z.string()
}).partial()

export const oauthGenericResponseSchema: z.ZodType<OauthGenericResponse> = z.strictObject({
  message: z.string(),
  path: z.string(),
  code: z.number()
}).partial()

export const oauthCodeResponseSchema: z.ZodType<OauthCodeResponse> = z.strictObject({
  code: z.string(),
  state: z.string(),
  redirect_uri: z.string()
}).partial()

const socialLoginSchema = socialSchema
const userDetailLoginSchema = userDetailSchema

// const fieldOptionLoginDecoder: D.Decoder<unknown, FieldOption> = fieldOptionDecoder

// const userDataUpdateFieldLoginDecoder: D.Decoder<unknown, UserDataUpdateField> = userDataUpdateFieldDecoder

const userDataUpdateResponseLoginSchema = userDataUpdateSchema

export const loginResponseLoginSchema: z.ZodType<LoginResponseLogin> = z.object({
  ua: z.string().optional(),
  authorization: z.string().optional(),
  refreshToken: z.string().optional(),
  ente_accreditato: z.boolean().optional(),
  social: socialLoginSchema.optional(),
  raisso: userDetailLoginSchema.optional(),
  user_data_update: userDataUpdateResponseLoginSchema.optional(),
  user_update: z.array(userUpdateSchema).optional().or(z.null()),
  response: z.string().optional(),
}).merge(genericResponseType);

export const deviceResponseSchema: z.ZodType<DeviceResponse> = z.strictObject({
  rtkn: z.string(),
  appID: z.number(),
  pin: z.number().gte(0),
  qrCode: z.string(),
  url: z.string()
}).extend(genericResponseType.shape).partial()

export const deviceSchema: z.ZodType<Device> = z.strictObject({
  id: z.number(),
  brand: z.string(),
  model: z.string(),
  'model-id': z.string(),
  domain: z.string(),
  users: z.array(z.number()).min(0),
  created: dateString.optional(),
  modified: dateTimeString.optional()
}).partial();

export const deviceDetailSchema: z.ZodType<DeviceDetail> = z.strictObject({
  uid: z.number(),
  marca: z.string().optional(),
  modello: z.string().optional(),
  deviceID: z.string().optional(),
  dataCreazione: dateTimeString.optional(),
  dataModifica: dateTimeString.optional(),
  dominio: z.string().optional()
})

export const downloadResponseSchema: z.ZodType<DownloadResponse> =
  z.union([
    z.object({
      status: z.enum(["NOT_PRESENT", "PENDING", "PROCESSED", "ERROR"])
    }),
    z.object({
      status: z.literal('READY'),
      link: z.string()
    })
  ]);

